// v2 API: `init` is a named export; `FullStory` is the callable FSApi function.
import { init, FullStory } from '@fullstory/browser';
import { env } from './env';

/**
 * Initialize FullStory. No-ops if FULLSTORY_ORG_ID is not set.
 * Call once at app startup from main.ts.
 */
export function initFullStory(): void {
  if (!env.FULLSTORY_ORG_ID) return;

  init({
    orgId: env.FULLSTORY_ORG_ID,
    // Don't auto-capture in dev so we don't pollute production session data
    devMode: import.meta.env.DEV,
  });
}

/**
 * Identify an authenticated user in FullStory so sessions are linked to real accounts.
 * Call after a successful profile fetch/sync.
 */
export function identifyFullStoryUser(user: {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organization: string;
  organizationId?: string;
}): void {
  if (!env.FULLSTORY_ORG_ID) return;

  // v2 API: FullStory('setIdentity', { uid, properties })
  FullStory('setIdentity', {
    uid: user.id,
    properties: {
      displayName: `${user.firstName} ${user.lastName}`,
      email: user.email,
      role_str: user.role,
      organization_str: user.organization,
      organizationId_str: user.organizationId ?? '',
    },
  });
}

/**
 * Anonymize the current session in FullStory on logout.
 * Stops associating future events with the previous user identity.
 */
export function anonymizeFullStoryUser(): void {
  if (!env.FULLSTORY_ORG_ID) return;
  // v2 API: FullStory('setIdentity', { anonymous: true })
  FullStory('setIdentity', { anonymous: true });
}

/**
 * URL of the current FullStory replay at this moment, for linking error
 * reports to what the user saw. Undefined when FullStory is off or not ready.
 */
export function getFullStorySessionUrl(): string | undefined {
  if (!env.FULLSTORY_ORG_ID) return undefined;
  try {
    return FullStory('getSession', { format: 'url.now' }) ?? undefined;
  } catch {
    return undefined;
  }
}
